import { deepEqual, newId } from "@pack-it-up/shared";
import { categoryKind } from "../entities/category.js";
import { contentEquals, snapshotOf } from "../entities/provenance.js";
import { dedupeKey, importTemplates, makeAssignmentResolver, normalizeName, resolveTemplateParticipants, sourceKey, } from "./importTemplates.js";
import { resolve } from "./relativeOffset.js";
// Stable identity for an entry: a template row is in at most one entry.
export const refreshEntryKey = (e) => `${e.collection}:${e.templateRowId}`;
const CHECKED_BY_DEFAULT = {
    unchanged: false,
    add: true,
    update: true,
    conflict: false,
};
// A ticked conflict writes the template's value, exactly as a ticked update does — that
// is what ticking it means. Only the default differs.
const writes = (status) => status === "update" || status === "conflict";
const catKey = (name, kind) => `${kind} ${normalizeName(name)}`;
// Doc 14 §3. `base` is the frozen import snapshot, absent when this template has never
// claimed the row (a dedupe-key match) — with no base a refresh can only leave the row
// alone or flag it, never silently overwrite.
const classify = (ours, theirs, base) => {
    if (contentEquals(ours, theirs))
        return "unchanged";
    if (base === undefined)
        return "conflict";
    if (contentEquals(ours, base))
        return "update";
    return "conflict";
};
const snapshotOfTemplateItem = (ti, assignment) => {
    const snap = { name: ti.name, assignment, quantity: ti.quantity };
    if (ti.description !== undefined)
        snap.description = ti.description;
    if (ti.notes !== undefined)
        snap.notes = ti.notes;
    if (ti.tags !== undefined)
        snap.tags = ti.tags;
    return snap;
};
const snapshotOfTemplateTodo = (tt, assignment) => {
    const snap = { name: tt.name, assignment };
    if (tt.description !== undefined)
        snap.description = tt.description;
    if (tt.notes !== undefined)
        snap.notes = tt.notes;
    if (tt.tags !== undefined)
        snap.tags = tt.tags;
    if (tt.relativeDueOffset !== undefined)
        snap.relativeDueOffset = tt.relativeDueOffset;
    return snap;
};
// The resolution the importer would apply, so a `specific` template assignment is
// compared against the trip in the form it would actually land in.
const resolvedAssignment = (raw, resolveIt) => {
    const assignment = resolveIt(raw);
    const skipped = raw.kind === "specific" &&
        (assignment.kind !== "specific" || assignment.allocations.length !== raw.allocations.length);
    return { assignment, skipped };
};
const makeMatcher = (templateId, template, tripContents) => {
    const templateCatName = new Map(template.categories.map((c) => [c.id, c.name]));
    const tripCatByKey = new Map();
    for (const c of tripContents.categories)
        tripCatByKey.set(catKey(c.name, categoryKind(c)), c.id);
    const bySource = new Map();
    for (const row of [...tripContents.items, ...tripContents.todos])
        for (const s of row.provenance?.templateSources ?? [])
            bySource.set(sourceKey(s.templateId, s.templateItemId), row);
    // Only rows no source has claimed are eligible for the dedupe fallback, so a claimed
    // row is never matched twice.
    const claimed = new Set([...bySource.values()].map((r) => r.id));
    const byDedupe = new Map();
    for (const row of [...tripContents.items, ...tripContents.todos]) {
        if (claimed.has(row.id))
            continue;
        const key = `${row.categoryId} ${dedupeKey(row.name, row.description, row.notes)}`;
        if (!byDedupe.has(key))
            byDedupe.set(key, row);
    }
    const match = (rowId, categoryId, name, description, notes, kind, is) => {
        const claim = bySource.get(sourceKey(templateId, rowId));
        if (claim)
            return is(claim) ? claim : undefined;
        const tripCat = tripCatByKey.get(catKey(templateCatName.get(categoryId) ?? "Uncategorised", kind));
        if (tripCat === undefined)
            return undefined;
        const hit = byDedupe.get(`${tripCat} ${dedupeKey(name, description, notes)}`);
        return hit && is(hit) ? hit : undefined;
    };
    const isItem = (r) => "quantity" in r;
    const isTodo = (r) => !("quantity" in r);
    return {
        items: (ti) => match(ti.id, ti.categoryId, ti.name, ti.description, ti.notes, "packing", isItem),
        todos: (tt) => match(tt.id, tt.categoryId, tt.name, tt.description, tt.notes, "todo", isTodo),
    };
};
// The base for the three-way compare: the snapshot frozen when this template last
// claimed the row. A row matched only by dedupe key has none (Doc 14 §3).
const baseFor = (row, templateId) => row.provenance?.templateSources.some((s) => s.templateId === templateId)
    ? row.provenance.importedSnapshot
    : undefined;
// --- draft ----------------------------------------------------------------
export const computeRefreshDraft = (templateId, trip, tripContents, template, actor) => {
    const resolveIt = makeAssignmentResolver(trip, resolveTemplateParticipants(trip, [template], actor));
    const matcher = makeMatcher(templateId, template, tripContents);
    const catName = new Map(template.categories.map((c) => [c.id, c.name]));
    const entries = [];
    const push = (collection, templateRowId, categoryId, name, templateValue, assignmentSkipped, tripRow) => {
        // Doc 14 §4 — an unresolvable `specific` assignment is not written, so it must not
        // count as a difference either: compare (and later patch) against the trip's own.
        const value = assignmentSkipped && tripRow !== undefined
            ? { ...templateValue, assignment: tripRow.assignment }
            : templateValue;
        const status = tripRow === undefined ? "add" : classify(snapshotOf(tripRow), value, baseFor(tripRow, templateId));
        entries.push({
            collection,
            status,
            templateRowId,
            ...(tripRow !== undefined ? { tripRowId: tripRow.id } : {}),
            categoryName: catName.get(categoryId) ?? "Uncategorised",
            name,
            ...(tripRow !== undefined ? { currentValue: snapshotOf(tripRow) } : {}),
            templateValue: value,
            assignmentSkipped: assignmentSkipped && status !== "unchanged",
            defaultChecked: CHECKED_BY_DEFAULT[status],
        });
    };
    for (const ti of template.items) {
        const { assignment, skipped } = resolvedAssignment(ti.assignment, resolveIt);
        push("items", ti.id, ti.categoryId, ti.name, snapshotOfTemplateItem(ti, assignment), skipped, matcher.items(ti));
    }
    for (const tt of template.todos) {
        const { assignment, skipped } = resolvedAssignment(tt.assignment, resolveIt);
        push("todos", tt.id, tt.categoryId, tt.name, snapshotOfTemplateTodo(tt, assignment), skipped, matcher.todos(tt));
    }
    return { templateId, entries };
};
export const refreshCounts = (draft, checked) => {
    let adds = 0;
    let updates = 0;
    let conflicts = 0;
    for (const e of draft.entries) {
        if (e.status === "conflict")
            conflicts += 1;
        if (!checked.has(refreshEntryKey(e)))
            continue;
        if (e.status === "add")
            adds += 1;
        else if (writes(e.status))
            updates += 1;
    }
    return { adds, updates, conflicts };
};
// --- plan -----------------------------------------------------------------
// `null` clears; `undefined` (an absent key) leaves the field alone.
const patchValue = (ours, theirs) => {
    if (theirs === undefined)
        return ours === undefined ? undefined : null;
    return deepEqual(ours, theirs) ? undefined : theirs;
};
export const planRefresh = (draft, trip, tripContents, template, checked, actor, idGen = newId) => {
    const byKey = new Map(draft.entries.map((e) => [refreshEntryKey(e), e]));
    const entryFor = (collection, rowId) => byKey.get(`${collection}:${rowId}`);
    const isTicked = (e) => checked.has(refreshEntryKey(e));
    // An unticked `add` is dropped from the input entirely, so the importer neither
    // creates it nor claims a trip row for it. Everything else stays: a matched row must
    // still pass through so its source is recorded (Doc 14 §6).
    const keep = (collection, rowId) => {
        const e = entryFor(collection, rowId);
        if (e === undefined)
            return false;
        return e.status !== "add" || isTicked(e);
    };
    const filtered = {
        ...template,
        items: template.items.filter((ti) => keep("items", ti.id)),
        todos: template.todos.filter((tt) => keep("todos", tt.id)),
    };
    const plan = {
        ...importTemplates(trip, [filtered], tripContents, actor, idGen),
        itemUpdates: [],
        todoUpdates: [],
    };
    const itemById = new Map(tripContents.items.map((i) => [i.id, i]));
    const todoById = new Map(tripContents.todos.map((t) => [t.id, t]));
    // Rebase the row's provenance onto what it will contain once the patch lands, folding
    // into the importer's own patch for that row when it emitted one.
    const rebase = (collection, row, snapshot) => {
        const existing = plan.provenancePatches.find((p) => p.collection === collection && p.id === row.id);
        const provenance = {
            ...(existing?.provenance ?? row.provenance ?? {
                templateSources: [],
                createdByImport: false,
                importedSnapshot: snapshot,
            }),
            importedSnapshot: snapshot,
            modifiedSinceImport: false,
        };
        if (existing)
            existing.provenance = provenance;
        else {
            const patch = {
                collection,
                id: row.id,
                provenance,
                templateIds: row.templateIds ?? [],
            };
            plan.provenancePatches.push(patch);
        }
    };
    for (const ti of template.items) {
        const e = entryFor("items", ti.id);
        if (e === undefined || !writes(e.status) || !isTicked(e))
            continue;
        const row = itemById.get(String(e.tripRowId));
        if (row === undefined)
            continue;
        const ours = snapshotOf(row);
        const theirs = e.templateValue;
        const patch = {};
        if (ours.name !== theirs.name)
            patch.name = theirs.name;
        if (theirs.quantity !== undefined && ours.quantity !== theirs.quantity)
            patch.quantity = theirs.quantity;
        const description = patchValue(ours.description, theirs.description);
        if (description !== undefined)
            patch.description = description;
        const notes = patchValue(ours.notes, theirs.notes);
        if (notes !== undefined)
            patch.notes = notes;
        const tags = patchValue(ours.tags, theirs.tags);
        if (tags !== undefined)
            patch.tags = tags;
        if (!e.assignmentSkipped && !deepEqual(ours.assignment, theirs.assignment))
            patch.assignment = theirs.assignment;
        if (Object.keys(patch).length === 0)
            continue;
        plan.itemUpdates.push({ id: row.id, patch });
        rebase("items", row, theirs);
    }
    for (const tt of template.todos) {
        const e = entryFor("todos", tt.id);
        if (e === undefined || !writes(e.status) || !isTicked(e))
            continue;
        const row = todoById.get(String(e.tripRowId));
        if (row === undefined)
            continue;
        const ours = snapshotOf(row);
        const theirs = e.templateValue;
        const patch = {};
        if (ours.name !== theirs.name)
            patch.name = theirs.name;
        const description = patchValue(ours.description, theirs.description);
        if (description !== undefined)
            patch.description = description;
        const notes = patchValue(ours.notes, theirs.notes);
        if (notes !== undefined)
            patch.notes = notes;
        const tags = patchValue(ours.tags, theirs.tags);
        if (tags !== undefined)
            patch.tags = tags;
        if (!e.assignmentSkipped && !deepEqual(ours.assignment, theirs.assignment))
            patch.assignment = theirs.assignment;
        const offset = patchValue(ours.relativeDueOffset, theirs.relativeDueOffset);
        if (offset !== undefined) {
            patch.relativeDueOffset = offset;
            // Doc 07 §4 — a hand-edited date wins over the template's offset; otherwise the
            // date follows the offset, and a cleared offset falls back to the trip's start.
            if (!row.dueDateManuallyOverridden && trip.startDate != null)
                patch.dueDate = offset === null ? trip.startDate : resolve(trip.startDate, offset);
        }
        if (Object.keys(patch).length === 0)
            continue;
        plan.todoUpdates.push({ id: row.id, patch });
        rebase("todos", row, theirs);
    }
    return plan;
};
//# sourceMappingURL=refreshTemplate.js.map